import { Suspense, lazy, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { getArticle } from "@/data/articles";
import ArticleLayout from "@/components/ArticleLayout";

/**
 * Route: /articles/:slug
 *
 * We keep every article as its own JSX body file under `./articles/`
 * and lazy-load it based on the URL slug. This means:
 *   - Adding a new article = create the JSX body + add manifest entry
 *   - Initial /articles listing bundle stays tiny
 *   - Each article gets its own code-split chunk
 *
 * If the slug isn't in the manifest we redirect to /articles.
 */

// Static map is required because Webpack cannot code-split with a fully
// dynamic import path. Every new article gets one line added here.
const BODIES = {
  "ai-tool-for-kundan-jewellery-design": lazy(() => import("./articles/AiToolForKundanJewelleryDesign.jsx")),
  "ai-tool-for-jadau-jewellery-design": lazy(() => import("./articles/AiToolForJadauJewelleryDesign.jsx")),
  "ai-tool-for-polki-diamond-jewellery-design": lazy(() => import("./articles/AiToolForPolkiDiamondJewelleryDesign.jsx")),
  "ai-diamond-jewellery-design-tool": lazy(() => import("./articles/AiDiamondJewelleryDesignTool.jsx")),
  "ai-temple-jewellery-design-tool": lazy(() => import("./articles/AiTempleJewelleryDesignTool.jsx")),
  "best-ai-tools-jewellery-sketch-to-cad": lazy(() => import("./articles/BestAiToolsSketchToCad.jsx")),
  "how-to-generate-photorealistic-jewellery-renders": lazy(() => import("./articles/HowToGeneratePhotorealRenders.jsx")),
  "software-for-indian-jewellery-designers": lazy(() => import("./articles/SoftwareForIndianDesigners.jsx")),
  "sketch-to-3d-workflow-for-jewellery-designers": lazy(() => import("./articles/SketchTo3dWorkflow.jsx")),
  "ai-multi-view-generator-for-jewellery": lazy(() => import("./articles/AiMultiViewGenerator.jsx")),
  "difference-jadau-kundan-polki": lazy(() => import("./articles/DifferenceJadauKundanPolki.jsx")),
  "prepare-cad-ready-renders-for-model-maker": lazy(() => import("./articles/PrepareCadReadyRenders.jsx")),
  "wall-thickness-guidelines-cast-jewellery": lazy(() => import("./articles/WallThicknessGuidelines.jsx")),
  "bill-of-materials-kundan-necklace-example": lazy(() => import("./articles/BomKundanNecklaceExample.jsx")),
  "render-meenakari-enamel-photorealistically": lazy(() => import("./articles/RenderMeenakariEnamel.jsx")),
  "facetra-vs-rhino-jewel": lazy(() => import("./articles/FacetraVsRhinoJewel.jsx")),
  "facetra-vs-midjourney-for-jewellery": lazy(() => import("./articles/FacetraVsMidjourney.jsx")),
  "matrixgold-vs-rhino-vs-facetra": lazy(() => import("./articles/MatrixgoldVsRhinoVsFacetra.jsx")),
  "ai-vs-traditional-cad-hand-drawn-sketches": lazy(() => import("./articles/AiVsTraditionalCad.jsx")),
  "best-3d-generators-for-jewellery": lazy(() => import("./articles/Best3dGenerators.jsx")),
  "preparing-sketches-for-ai-jewellery-rendering": lazy(() => import("./articles/PreparingSketchesForAiJewelleryRendering.jsx")),
  "stl-vs-obj-vs-glb-vs-fbx-for-jewellery-casting": lazy(() => import("./articles/StlVsObjVsGlbVsFbx.jsx")),
  "jewellery-portfolio-that-ranks-and-gets-hired": lazy(() => import("./articles/JewelleryPortfolioThatRanks.jsx")),
  "model-vs-creative-vs-custom-sizing-photoshoot": lazy(() => import("./articles/ModelVsCreativeVsCustomSizing.jsx")),
  "hd-vs-ultra-hd-vs-4k-vs-print-master-jewellery-photo": lazy(() => import("./articles/HdVsUltraHdVs4kVsPrintMaster.jsx")),
  // 2026-02-25 SEO §29 cluster articles for photo / catalog / portfolio / technical pillars.
  "how-to-edit-jewellery-product-photos-with-ai": lazy(() => import("./articles/HowToEditJewelleryProductPhotosWithAi.jsx")),
  "jewellery-photo-retouching-guide": lazy(() => import("./articles/JewelleryPhotoRetouchingGuide.jsx")),
  "how-to-prepare-jewellery-images-for-ecommerce": lazy(() => import("./articles/HowToPrepareJewelleryImagesForEcommerce.jsx")),
  "how-to-create-jewellery-catalog-with-ai": lazy(() => import("./articles/HowToCreateJewelleryCatalogWithAi.jsx")),
  "ai-jewellery-product-photography": lazy(() => import("./articles/AiJewelleryProductPhotography.jsx")),
  "how-to-create-consistent-jewellery-catalog-images": lazy(() => import("./articles/HowToCreateConsistentJewelleryCatalogImages.jsx")),
  "how-to-create-jewellery-design-portfolio": lazy(() => import("./articles/HowToCreateJewelleryDesignPortfolio.jsx")),
  "jewellery-design-portfolio-with-technical-drawing": lazy(() => import("./articles/JewelleryDesignPortfolioWithTechnicalDrawing.jsx")),
  "how-to-present-jewellery-design-concepts": lazy(() => import("./articles/HowToPresentJewelleryDesignConcepts.jsx")),
  "jewellery-technical-drawing-guide": lazy(() => import("./articles/JewelleryTechnicalDrawingGuide.jsx")),
  "front-side-top-view-jewellery-drawing": lazy(() => import("./articles/FrontSideTopViewJewelleryDrawing.jsx")),
};

export default function Article() {
  const { slug } = useParams();
  const meta = useMemo(() => getArticle(slug), [slug]);

  if (!meta || !BODIES[slug]) return <Navigate to="/articles" replace />;

  const Body = BODIES[slug];

  return (
    <ArticleLayout meta={meta}>
      <Suspense fallback={<p className="text-white/40 text-sm">Loading article…</p>}>
        <Body />
      </Suspense>
    </ArticleLayout>
  );
}
